/**
 * General (Figma 09 · Form pages 10–11): the workspace's name and who owns
 * it. Not built, no backend yet: asking Mohtawa to hand the workspace over.
 */
import { useState } from "react";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useGetBrands } from "@/hooks/useGetBrands";
import { useOrganizationMembers, useRenameOrganization } from "@/app/routes/dashboard/settings/api/useOrganization";
import { KitButton } from "../../kit/Button";
import { KitInput } from "../../kit/Input";
import {
  Card,
  DetailHead,
  Fact,
  Facts,
  PageHead,
  QuietNote,
  ScopeTile,
  SectionHead,
  SettingsPage,
} from "../parts/SettingsParts";
import { useWorkspaceHead } from "../parts/scope";
import { formatDay, plural } from "../parts/settingsFormat";
import { planName } from "./PlanPage";

// Same bounds as the backend's v_RenameOrganization.
const NAME_MIN = 5;
const NAME_MAX = 50;

export default function GeneralPage() {
  const head = useWorkspaceHead();
  const { selectedOrganization, user } = useCurrentUser();
  const org = selectedOrganization?.organization;
  const isOwner = selectedOrganization?.role === "Owner";
  const { data: brands } = useGetBrands();
  const { data: members } = useOrganizationMembers(org?.id ?? null);
  const rename = useRenameOrganization(org?.id ?? null);
  const [draft, setDraft] = useState<string | null>(null);

  const owner = members?.find((m) => m.role === "Owner");
  const ownerName = owner ? `${owner.name || owner.email}${owner.userId === user?.id ? " (you)" : ""}` : "";
  const name = draft ?? org?.name ?? "";
  const trimmed = name.trim();
  const invalid = trimmed.length < NAME_MIN || trimmed.length > NAME_MAX;
  const changed = trimmed !== (org?.name ?? "");

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (invalid || !changed) return;
    rename.mutate(trimmed);
    setDraft(null);
  };

  return (
    <SettingsPage>
      <PageHead {...head} title="General" status="The workspace’s name and who owns it." />
      <Card>
        <DetailHead
          lead={<ScopeTile name={org?.name ?? "?"} size={56} />}
          title={org?.name}
          sub={`Workspace${brands ? ` · ${plural(brands.length, "brand")}` : ""}`}
        />
        <Facts>
          {ownerName ? <Fact label="Owner">{ownerName}</Fact> : null}
          {org ? (
            <Fact label="Plan">
              {planName(org.paymentPlan)}
              {org.maxSeats ? `, ${plural(org.maxSeats, "seat")}` : ""}
            </Fact>
          ) : null}
          {org?.createdAt ? <Fact label="Made">{formatDay(org.createdAt)}</Fact> : null}
        </Facts>

        {isOwner ? (
          <>
            <form onSubmit={save} className="flex w-full max-w-[460px] flex-col gap-3">
              <KitInput
                label="Workspace name"
                value={name}
                maxLength={NAME_MAX}
                onChange={(e) => setDraft(e.target.value)}
                error={changed && invalid ? `Use ${NAME_MIN} to ${NAME_MAX} characters` : undefined}
              />
              <QuietNote>Everyone in the workspace and its clients see this name.</QuietNote>
              <KitButton type="submit" className="self-start" isDisabled={!changed || invalid}>
                Save name
              </KitButton>
            </form>
            <SectionHead title="Ownership" />
            <QuietNote>Handing the workspace to someone else is a request to Mohtawa for now.</QuietNote>
          </>
        ) : (
          <QuietNote>
            Only the owner{owner ? `, ${owner.name || owner.email},` : ""} can rename the workspace or hand it over.
          </QuietNote>
        )}
      </Card>
    </SettingsPage>
  );
}
